'use client'

/**
 * Whether the viewer has clicked Join for a meeting instance (#14931), and
 * the callback that records it — the one hook every Join surface reads, so a
 * click hides Join everywhere on the page at once and in other tabs through
 * the `storage` event. See `@/lib/meetings/meeting-joined-store`.
 *
 * The server snapshot is "not joined": the browser's memory is not the
 * server's to know, so the first client render agrees with the HTML and the
 * store's answer follows it.
 */

import { useCallback, useMemo, useSyncExternalStore } from 'react'

import type { JoinableConference } from '@/lib/meetings/meeting-join'
import {
  getMeetingJoinedSnapshot,
  isMeetingJoined,
  markMeetingJoined,
  subscribeMeetingJoined,
} from '@/lib/meetings/meeting-joined-store'

const SERVER_SNAPSHOT = 'server'

export function useMeetingJoined(
  conference: JoinableConference | null | undefined,
  nowMs: number,
): { joined: boolean; markJoined: () => void } {
  const snapshot = useSyncExternalStore(
    subscribeMeetingJoined,
    getMeetingJoinedSnapshot,
    () => SERVER_SNAPSHOT,
  )
  const url = conference?.url ?? null
  const startsAtIso = conference?.startsAtIso ?? null
  const endsAtIso = conference?.endsAtIso ?? null

  const joined = useMemo(() => {
    if (snapshot === SERVER_SNAPSHOT || url === null || startsAtIso === null) {
      return false
    }
    return isMeetingJoined(
      { eventId: '', url, provider: null, startsAtIso, endsAtIso },
      nowMs,
    )
    // `snapshot` is the store's version: a change re-reads the answer.
  }, [snapshot, url, startsAtIso, endsAtIso, nowMs])

  const markJoined = useCallback(() => {
    if (url === null || startsAtIso === null) return
    markMeetingJoined(
      { eventId: '', url, provider: null, startsAtIso, endsAtIso },
      Date.now(),
    )
  }, [url, startsAtIso, endsAtIso])

  return { joined, markJoined }
}
